import { StateMachine } from "@/core/arch/state_machine";
import { isTerminal } from "./job_polling";
import type { JobRecord } from "./types";

// ── Types ────────────────────────────────────────────────────────────────────

export type BackfillOutcome =
  "planned" | "written" | "alreadyInCpos" | "unenriched" | "skipped" | "error";

export interface BackfillOutcomeRow {
  poyntTransactionId: string;
  legIds: string[];
  createdAt: string;
  /** Cents. */
  amount: number;
  status: string;
  outcome: BackfillOutcome;
  reason?: string;
  /** Amounts in cents. */
  enrichment?: { staffId?: string; taxAmount?: number; processingFee?: number };
}

export const COUNTER_KEYS = [
  "poyntSales",
  "eligible",
  "written",
  "alreadyInCpos",
  "enriched",
  "unenriched",
  "possibleCashRemainder",
  "otherApp",
  "declined",
  "tooRecent",
  "notPreviewed",
  "unparseable",
  "errors",
] as const;

export type BackfillCounterKey = (typeof COUNTER_KEYS)[number];

/** What a `poynt-sale-backfill` job leaves in `Job.result`. */
export interface PoyntSaleBackfillResult {
  /** `unmapped` means the merchant has no linked Poynt business. */
  status: "ok" | "unmapped";
  poyntBusinessIds: string[];
  counters: Record<BackfillCounterKey, number>;
  /** Capped; the counters still cover every sale. */
  outcomes: BackfillOutcomeRow[];
  truncated: boolean;
  /** Dry run: every sale a write run against this preview may create. */
  plannedIds: string[];
  /** Write run: every row it created. Empty for a dry run. */
  writtenIds: string[];
  /** Day the run stopped at, possibly partly written; re-running it is idempotent (time budget, cancel, or Amplitude down on a write). */
  stoppedAt: string | null;
}

/** Reasons the runner gives a planned or written sale Amplitude added nothing to. */
const UNENRICHED_REASONS: ReadonlySet<string> = new Set([
  "noEvent",
  "duplicateEvent",
  "amountMismatch",
  "amplitudeUnavailable",
  "possibleCashRemainder",
]);

/** Whether a planned or written sale goes in without Amplitude's staff, tax and fee. */
export function isUnenriched(row: BackfillOutcomeRow): boolean {
  return (
    (row.outcome === "planned" || row.outcome === "written") &&
    row.reason !== undefined &&
    UNENRICHED_REASONS.has(row.reason)
  );
}

export interface PoyntSaleBackfillInput {
  businessId: string;
  from: string;
  to: string;
  poyntBusinessId?: string;
  dryRun: boolean;
  previewJobId?: string;
}

export interface PoyntSaleBackfillForm {
  businessId: string;
  from: string;
  to: string;
  poyntBusinessId: string;
}

/** A finished dry run: what it was asked, what it found, and its job. */
export interface BackfillPreview {
  input: PoyntSaleBackfillInput;
  result: PoyntSaleBackfillResult;
  previewJobId: string;
}

export type PoyntSaleBackfillPhase =
  | { kind: "composing" }
  /** The job is being enqueued; no id yet. `preview` is set when it is the write. */
  | { kind: "starting"; preview?: BackfillPreview }
  | { kind: "previewing"; jobId: string }
  | ({ kind: "previewed" } & BackfillPreview)
  | { kind: "writing"; jobId: string; preview: BackfillPreview }
  | {
      kind: "written";
      input: PoyntSaleBackfillInput;
      result: PoyntSaleBackfillResult;
      preview: BackfillPreview;
    }
  | { kind: "error"; message: string };

/** What the sheet shows for a result: the result, the input it answers, and the day to resume from. */
export interface ShownResult {
  result: PoyntSaleBackfillResult;
  input: PoyntSaleBackfillInput;
  stoppedAt: string | null;
}

const fromPreview = (p: BackfillPreview): ShownResult => ({
  result: p.result,
  input: p.input,
  stoppedAt: p.result.stoppedAt,
});

/**
 * The result to show in a phase, or null when there is none. A write keeps
 * its preview on screen until it finishes, and a finished write keeps the
 * preview's resume day when it has none of its own.
 */
export function shownResult(phase: PoyntSaleBackfillPhase): ShownResult | null {
  switch (phase.kind) {
    case "previewed":
      return fromPreview(phase);
    case "starting":
      return phase.preview ? fromPreview(phase.preview) : null;
    case "writing":
      return fromPreview(phase.preview);
    case "written":
      return {
        result: phase.result,
        input: phase.input,
        stoppedAt: phase.result.stoppedAt ?? phase.preview.result.stoppedAt,
      };
    case "composing":
    case "previewing":
    case "error":
      return null;
  }
}

export interface PoyntSaleBackfillState {
  phase: PoyntSaleBackfillPhase;
  form: PoyntSaleBackfillForm;
}

export interface PoyntSaleBackfillRepository {
  kickoff(input: PoyntSaleBackfillInput): Promise<string>;
  /** Reports each observed state of the job; returns a function that stops. */
  watch(jobId: string, onJob: (job: JobRecord) => void): () => void;
}

// ── Result parsing ───────────────────────────────────────────────────────────

const OUTCOMES: ReadonlySet<string> = new Set<BackfillOutcome>([
  "planned",
  "written",
  "alreadyInCpos",
  "unenriched",
  "skipped",
  "error",
]);

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : [];
}

function toOutcomeRow(raw: unknown): BackfillOutcomeRow {
  const r = asRecord(raw);
  const e = asRecord(r["enrichment"]);
  const num = (v: unknown) => (typeof v === "number" ? v : undefined);
  return {
    poyntTransactionId: String(r["poyntTransactionId"] ?? ""),
    legIds: strings(r["legIds"]),
    createdAt: String(r["createdAt"] ?? ""),
    amount: num(r["amount"]) ?? 0,
    status: String(r["status"] ?? ""),
    // An outcome this client does not know must not read as `planned`.
    outcome:
      typeof r["outcome"] === "string" && OUTCOMES.has(r["outcome"])
        ? (r["outcome"] as BackfillOutcome)
        : "error",
    reason: typeof r["reason"] === "string" ? r["reason"] : undefined,
    enrichment:
      Object.keys(e).length > 0
        ? {
            staffId:
              typeof e["staffId"] === "string" ? e["staffId"] : undefined,
            taxAmount: num(e["taxAmount"]),
            processingFee: num(e["processingFee"]),
          }
        : undefined,
  };
}

/**
 * Reads `Job.result`, which can arrive as an object, a JSON string, or a JSON
 * string of a JSON string. Returns null when it is not a result this client
 * understands, so the caller reports that instead of rendering guesses.
 */
export function parseBackfillResult(
  raw: unknown,
): PoyntSaleBackfillResult | null {
  let value = raw;
  for (let i = 0; i < 2 && typeof value === "string"; i++) {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  const r = asRecord(value);
  if (r["status"] !== "ok" && r["status"] !== "unmapped") return null;

  const c = asRecord(r["counters"]);
  const counters = Object.fromEntries(
    COUNTER_KEYS.map((key) => {
      // eslint-disable-next-line security/detect-object-injection -- key comes from the fixed COUNTER_KEYS list
      const n = c[key];
      return [key, typeof n === "number" ? n : 0];
    }),
  ) as Record<BackfillCounterKey, number>;

  return {
    status: r["status"],
    poyntBusinessIds: strings(r["poyntBusinessIds"]),
    counters,
    outcomes: Array.isArray(r["outcomes"])
      ? r["outcomes"].map(toOutcomeRow)
      : [],
    truncated: r["truncated"] === true,
    plannedIds: strings(r["plannedIds"]),
    writtenIds: strings(r["writtenIds"]),
    stoppedAt: typeof r["stoppedAt"] === "string" ? r["stoppedAt"] : null,
  };
}

// ── BLoC ─────────────────────────────────────────────────────────────────────

const EMPTY_FORM: PoyntSaleBackfillForm = {
  businessId: "",
  from: "",
  to: "",
  poyntBusinessId: "",
};

/**
 * Preview-then-write backfill of missing Poynt sales for one merchant.
 *
 * The write run is only ever enqueued against the preview the operator just
 * read: `write()` is refused outside `previewed` and sends the preview's own
 * input, so the previewed window and the written window cannot differ.
 */
export class PoyntSaleBackfillBloc extends StateMachine<PoyntSaleBackfillState> {
  private _unwatch: (() => void) | null = null;
  private _disposed = false;
  /** Bumped whenever the live watch is dropped, so its late callbacks are ignored. */
  private _attachment = 0;

  constructor(private readonly repo: PoyntSaleBackfillRepository) {
    super({ phase: { kind: "composing" }, form: EMPTY_FORM });
    this.appendDisposeCallback(this.teardown);
  }

  private readonly teardown = (): void => {
    this._disposed = true;
    this._detach();
  };

  /**
   * Returns a disposed bloc to service: `useStateMachine` keeps the instance
   * across React StrictMode's mount/cleanup/mount replay, and `dispose`
   * consumes its callbacks, so both the flag and the callback are restored.
   */
  private rearm(): void {
    this._disposed = false;
    this.appendDisposeCallback(this.teardown);
  }

  /** Ignored while a job is being started or watched; otherwise it drops any result. */
  setField(name: keyof PoyntSaleBackfillForm, value: string): void {
    const kind = this.state.phase.kind;
    if (kind === "starting" || kind === "previewing" || kind === "writing") {
      return;
    }
    this.setState({
      phase: { kind: "composing" },
      form: { ...this.state.form, [name]: value },
    });
  }

  async preview(): Promise<void> {
    if (this.state.phase.kind !== "composing") return;
    const { businessId, from, to } = this._trimmed();
    if (!businessId || !from || !to) return;
    await this._start(this._input(), null);
  }

  async write(): Promise<void> {
    const phase = this.state.phase;
    if (phase.kind !== "previewed") return;
    if (phase.result.status !== "ok" || phase.result.plannedIds.length === 0) {
      return;
    }
    const preview: BackfillPreview = {
      input: phase.input,
      result: phase.result,
      previewJobId: phase.previewJobId,
    };
    await this._start(
      { ...phase.input, dryRun: false, previewJobId: phase.previewJobId },
      preview,
    );
  }

  /** Back to the form, keeping what was typed. */
  reset(): void {
    this._detach();
    this.setState({ ...this.state, phase: { kind: "composing" } });
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private _trimmed(): PoyntSaleBackfillForm {
    const f = this.state.form;
    return {
      businessId: f.businessId.trim(),
      from: f.from.trim(),
      to: f.to.trim(),
      poyntBusinessId: f.poyntBusinessId.trim(),
    };
  }

  /** The dry-run input the form describes. */
  private _input(): PoyntSaleBackfillInput {
    const { businessId, from, to, poyntBusinessId } = this._trimmed();
    return {
      businessId,
      from,
      to,
      ...(poyntBusinessId ? { poyntBusinessId } : {}),
      dryRun: true,
    };
  }

  /** @param preview The preview being written; null for the dry run itself. */
  private async _start(
    input: PoyntSaleBackfillInput,
    preview: BackfillPreview | null,
  ): Promise<void> {
    if (this._disposed) this.rearm();
    this._detach();
    const attachment = this._attachment;
    // Leaves the phase before the await, so a second click is refused.
    this.setState({
      ...this.state,
      phase: preview ? { kind: "starting", preview } : { kind: "starting" },
    });
    let jobId: string;
    try {
      jobId = await this.repo.kickoff(input);
    } catch (err) {
      if (attachment !== this._attachment) return;
      this._fail(err instanceof Error ? err.message : String(err));
      return;
    }
    // The sheet closed or was reset while the job was being enqueued.
    if (this._disposed || attachment !== this._attachment) return;
    this.setState({
      ...this.state,
      phase: preview
        ? { kind: "writing", jobId, preview }
        : { kind: "previewing", jobId },
    });
    const unwatch = this.repo.watch(jobId, (job) => {
      if (attachment !== this._attachment) return;
      this._onJob(job, input, jobId, preview);
    });
    // A watch that reports synchronously has already settled the run.
    if (attachment === this._attachment) this._unwatch = unwatch;
    else unwatch();
  }

  private _onJob(
    job: JobRecord,
    input: PoyntSaleBackfillInput,
    jobId: string,
    preview: BackfillPreview | null,
  ): void {
    if (!isTerminal(job.status)) return;
    this._detach();
    if (job.status === "failed") {
      this._fail(job.error ?? "The job failed without a message.");
      return;
    }
    if (job.status === "cancelled") {
      this._fail("The job was cancelled.");
      return;
    }
    const result = parseBackfillResult(job.result);
    if (!result) {
      this._fail("The job finished without a result this tool can read.");
      return;
    }
    this.setState({
      ...this.state,
      phase: preview
        ? { kind: "written", input, result, preview }
        : { kind: "previewed", input, result, previewJobId: jobId },
    });
  }

  private _fail(message: string): void {
    this.setState({ ...this.state, phase: { kind: "error", message } });
  }

  private _detach(): void {
    this._attachment++;
    if (this._unwatch) {
      this._unwatch();
      this._unwatch = null;
    }
  }
}
